<template>
  <jet-form-section @submitted="updateProfileInformation">
    <template #title>Profile information</template>

    <template #description>Update profile information below and save changes.</template>

    <template #form>
      <div class="flex flex-wrap">
        <div class="flex w-full gap-2 items-center mb-2">
          <!-- Profile Photo -->
          <div class="flex-column w-44" v-if="$page.props.jetstream.managesProfilePhotos">
            <!-- Profile Photo File Input -->
            <input type="file" class="hidden" ref="photo" @change="updatePhotoPreview" />

            <!-- Current Profile Photo -->
            <div class="my-8 relative w-36 h-36">
              <ViceAvatar
                v-show="!photoPreview"
                :image="user.profile_photo_url"
                :name="user.name"
                class="w-full h-full text-lg"
              />

              <span
                v-show="photoPreview"
                class="block rounded-full w-full h-full new-avatar"
                :style="`background-image: url('${photoPreview}');`"
              ></span>

              <TrashcanAvatar
                class="absolute top-28 left-2 cursor-pointer"
                @click.prevent="deletePhoto"
                v-if="user.profile_photo_path"
              ></TrashcanAvatar>

              <PencilAvatar
                class="absolute top-28 left-24 cursor-pointer"
                @click.prevent="selectNewPhoto"
                type="button"
              ></PencilAvatar>
            </div>
            <jet-input-error :message="form.errors.photo" class="mt-2 absolute" />
          </div>

          <div class="flex-column w-5/6 space-y-8">
            <div class="flex w-full gap-6 justify-between">
              <!-- Title -->
              <div class="flex-column w-1/4">
                <jet-label for="title" value="Title" />
                <jet-input
                  data-cy="clinician-edit-title"
                  id="title"
                  type="text"
                  class="mt-1 block w-full"
                  v-model="form.title"
                  ref="title"
                  autocomplete="title"
                />
                <jet-input-error :message="form.errors.title" class="mt-2" />
              </div>

              <!-- Name -->
              <div class="flex-column w-2/5">
                <jet-label for="name" value="Name" />
                <jet-input
                  data-cy="clinician-edit-name"
                  id="name"
                  type="text"
                  class="mt-1 block w-full"
                  v-model="form.name"
                  autocomplete="name"
                />
                <jet-input-error :message="form.errors.name" class="mt-2" />
              </div>

              <!-- Position-->
              <div class="flex-column w-2/5">
                <jet-label for="position" value="Position" />
                <jet-input
                  data-cy="clinician-edit-position"
                  id="position"
                  type="text"
                  class="mt-1 block w-full"
                  v-model="form.position"
                  ref="position"
                  autocomplete="position"
                />
                <jet-input-error :message="form.errors.position" class="mt-2" />
              </div>
            </div>

            <div class="flex w-full gap-6">
              <!-- Email -->
              <div class="w-1/2">
                <jet-label for="email" value="Email" />
                <jet-input
                  data-cy="clinician-edit-email"
                  id="email"
                  type="email"
                  class="mt-1 block w-full"
                  v-model="form.email"
                  autocomplete="on"
                />
                <jet-input-error :message="form.errors.email" class="mt-2" />
              </div>

              <!-- Phone -->
              <div class="w-1/2">
                <jet-label for="phone" value="Phone" />
                <vue-tel-input
                  data-cy="clinician-edit-phone"
                  v-model="form.phone"
                  id="phone"
                  ref="phone"
                  class="mt-1 h-9"
                  :defaultCountry="'GB'"
                  :mode="'international'"
                  :ignoredCountries="['RU']"
                  :inputOptions="{ showDialCode: true }"
                ></vue-tel-input>
                <jet-input-error :message="form.errors.phone" class="mt-2" />
              </div>
            </div>
          </div>
        </div>

        <!-- About-->
        <div class="flex w-full mb-8">
          <div class="flex-column w-full">
            <jet-label for="intro_about" value="About myself in short" />
            <textarea
              data-cy="clinician-edit-about"
              v-model="form.intro_about"
              class="
                focus:ring-vice-teal-500
                focus:border-vice-teal-500
                block
                w-full
                sm:text-sm
                border-gray-300
                rounded-md
              "
              rows="4"
            />
            <span class="block text-sm text-gray-500">
              Provide a brief description of yourself.
            </span>
            <jet-input-error :message="form.errors.intro_about" class="mt-2" />
          </div>
        </div>

        <!-- More -->
        <div class="flex w-full mb-8">
          <div class="flex-column w-full">
            <jet-label for="read_more_about" value="More about my self" />
            <textarea
              data-cy="clinician-read-more"
              v-model="form.read_more_about"
              class="
                focus:ring-vice-teal-500
                focus:border-vice-teal-500
                block
                w-full
                sm:text-sm
                border-gray-300
                rounded-md
              "
              rows="4"
            />
            <span class="block text-sm text-gray-500">
              Provide a additional information of yourself.
            </span>
            <jet-input-error :message="form.errors.read_more_about" class="mt-2" />
          </div>
        </div>
      </div>
    </template>

    <template #actions>
      <action-message
        :on="form.recentlySuccessful"
        message="Your profile changes have been updated."
      ></action-message>

      <ViceButton
        data-cy="clinician-save-changes"
        class="px-4 border border-vice-light-grey"
        :class="{ 'opacity-25': form.processing || isUploading }"
        :disabled="form.processing || isUploading"
        medium
      >
        Save
      </ViceButton>
    </template>
  </jet-form-section>
</template>

<script>
import imageCompression from 'browser-image-compression';
import { VueTelInput } from 'vue-tel-input';
import JetFormSection from '@/Jetstream/FormSection.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import JetLabel from '@/Jetstream/Label.vue';
import ActionMessage from '@/Jetstream/ActionMessage.vue';
import ViceAvatar from '@/Components/Avatars/ViceAvatar.vue';
import TrashcanAvatar from '@/Components/Icons/TrashcanAvatar.vue';
import PencilAvatar from '@/Components/Icons/PencilAvatar.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';

export default {
  components: {
    ViceButton,
    PencilAvatar,
    TrashcanAvatar,
    ActionMessage,
    JetFormSection,
    JetInput,
    JetInputError,
    JetLabel,
    ViceAvatar,
    VueTelInput,
  },

  props: ['user', 'clinicianDetail'],

  data() {
    return {
      form: this.$inertia.form({
        _method: 'PUT',
        name: this.user.name,
        email: this.user.email,
        phone: this.user.phone,
        photo: null,
        title: this.$page.props.clinicianDetail?.title ?? '',
        position: this.$page.props.clinicianDetail?.position ?? '',
        intro_about: this.$page.props.clinicianDetail?.intro_about,
        read_more_about: this.$page.props.clinicianDetail?.read_more_about,
      }),

      photoPreview: null,
      isUploading: false,
    };
  },
  methods: {
    async updateProfileInformation() {
      if (this.$refs.photo && this.$refs.photo.files[0]) {
        const photo = this.$refs.photo.files[0];
        const targetSize = 1024 * 1024;

        if (photo.size > targetSize) {
          this.isUploading = true;
          this.form.photo = await this.compressImage(photo, targetSize);
          this.isUploading = false;
        } else {
          this.form.photo = photo;
        }
      }

      this.form.post(route('clinician-profile-information.update'), {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onSuccess: () => {
          this.clearPhotoFileInput();
          this.isUploading = false;
        },
        onError: () => {
          this.isUploading = false;
        },
      });
    },

    selectNewPhoto() {
      this.$refs.photo.click();
    },

    updatePhotoPreview() {
      const photo = this.$refs.photo.files[0];

      if (!photo) return;

      const reader = new FileReader();

      reader.onload = (e) => {
        this.photoPreview = e.target.result;
      };

      reader.readAsDataURL(photo);
    },

    async compressImage(file, targetSize) {
      const quality = 0.9;

      const compress = async (inputFile, currentQuality) => {
        if (inputFile.size <= targetSize || currentQuality <= 0.1) {
          return inputFile;
        }

        const compressedFile = await imageCompression(inputFile, {
          maxSizeMB: targetSize / (1024 * 1024),
          useWebWorker: true,
          maxWidthOrHeight: 8000,
          initialQuality: currentQuality,
        });

        return compress(compressedFile, currentQuality - 0.1);
      };

      return compress(file, quality);
    },

    deletePhoto() {
      this.$inertia.post(route('clinician.current-user-photo.destroy'), {
        preserveScroll: true,
        onSuccess: () => {
          this.photoPreview = null;
          this.clearPhotoFileInput();
        },
      });
    },

    clearPhotoFileInput() {
      if (this.$refs.photo?.value) {
        this.$refs.photo.value = null;
      }
    },
  },
  watch: {
    'form.phone': {
      handler(newVal) {
        if (newVal) this.form.phone = this.form.phone.replace(/\s+/g, '');
      },
    },
  },
};
</script>

<style scoped>
.new-avatar {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
}
</style>
